import { ErrorMessage } from 'views/components';
import { InstructionsContainer } from './styled';
import PropTypes from 'prop-types';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import { patientActions } from 'core/Patient';
import { useTranslation } from 'react-i18next';
import { validationSchema } from 'shared/utils';
import {
    ButtonFooter,
    DownloadButton,
    DragAndDrop,
    ExpandButton,
    FormGroup,
    Modal,
    ProgressBar,
    Table,
    Text
} from 'mol-ui';
import {
    CompanyPicker,
    OfficePicker
} from 'views/pickers';
import {
    HEADERS,
    HELP_ITEMS
} from './fixtures';
import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useErrors,
    useInput,
    useSelector
} from 'hooks';
const {
    company,
    patientImportOffice
} = config;

const PatientImportModal = ({
    active,
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        configurations: {
            import_fields
        }
    } = useSelector('config');
    const {
        importErrors,
        importGlobalError,
        importInfo,
        importLoading,
        importSucceded: {
            succeded,
            updated
        },
        importUploading
    } = useSelector('patient');
    const [file, setFile] = useState(null);
    const [companyInput, companyId] = useInput(null, {}, () => clearError('companyId'));
    const [officeInput, officeId] = useInput(null, {}, () => clearError('officeId'));
    const { clearError, errors, setErrors } = useErrors();

    useEffect(() => {
        if (active) {
            dispatch(patientActions.setPatientImportErrors([]));
            dispatch(patientActions.setPatientImportGlobalError(''));
            dispatch(patientActions.setPatientImportInfo({}));
            dispatch(patientActions.setPatientImportSucceded({}));
        }
    }, [active]);

    const modalProps = {
        active,
        onToggle,
        width: isEmpty(importErrors) ? 'auto' : '100%'
    };

    const onFileUpload = () => {
        if (!validate()) return;
        dispatch(patientActions.importPatients({
            company_id: companyId,
            file,
            office_id: officeId
        }));
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            companyId: [!company || !!companyId, t('Requerido')],
            officeId: [!patientImportOffice || !!officeId, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const showError = isEmpty(importErrors) ? undefined : `${ t('Ocurrió un error al intentar importar los siguientes registros') }:`;
    const finished = (!!succeded || !!updated);

    const parsedHeaders = import_fields.map(field => ({
        key: field,
        label: HEADERS[field]
    }));

    const DOWNLOAD_HEADERS = [...parsedHeaders, {
        flex: 3,
        key: 'error',
        label: t('Error')
    }];

    return (
        <Modal { ...modalProps }>
            {
                (!finished || !isEmpty(importErrors)) && !importLoading &&
                <>
                    <DownloadButton
                        caption={ t('Descargar plantilla') }
                        data={ [{}] }
                        fileName={ t('Plantilla de importación') }
                        fontSize="12px"
                        headers={ parsedHeaders }
                        margin="0px 0px 10px 0px"
                        separator=";"
                        size={ 20 }
                        target="_blank"
                    />
                    <InstructionsContainer>
                        <li>{ t('Archivo CSV separado por punto y coma') }</li>
                        {
                            HELP_ITEMS.map(({ key, label, text }) => {
                                if (!import_fields.includes(key)) return null;
                                return (
                                    <li key={ key }>{`${ label }: ${ text }`}</li>
                                );
                            })
                        }
                    </InstructionsContainer>
                    {
                        company &&
                        <FormGroup label={ t('Empresa') } width="100%">
                            <CompanyPicker
                                { ...companyInput }
                                hasError={ !!errors?.companyId }
                                margin="0px 0px 5px 0px"
                            />
                            <ErrorMessage relative error={ errors?.companyId } marginLeft={ 0 } marginTop={ 0 } />
                        </FormGroup>
                    }
                    {
                        patientImportOffice &&
                        <FormGroup label={ t('Consultorio') } margin="10px 0px 0px 0px" width="100%">
                            <OfficePicker
                                { ...officeInput }
                                hasError={ !!errors?.officeId }
                                margin="0px 0px 5px 0px"
                            />
                            <ErrorMessage relative error={ errors?.officeId } marginLeft={ 0 } marginTop={ 0 } />
                        </FormGroup>
                    }
                    <DragAndDrop
                        margin="10px 0px 10px 0px"
                        width="320px"
                        onFileChange={ setFile }
                    />
                    <ErrorMessage relative error={ importGlobalError } marginLeft={ 0 } marginTop={ 10 } />
                </>
            }
            {
                !isEmpty(importInfo) &&
                <ProgressBar
                    error={ !isEmpty(importErrors) }
                    info={ finished ? undefined : `${ importInfo?.processed_count }/${ importInfo?.total_count }` }
                    margin="20px 0px 20px 0px"
                    max={ importInfo?.total_count }
                    success={ finished && isEmpty(importErrors) }
                    title={ `${ t('Importando pacientes') }...` }
                    value={ importInfo?.processed_count }
                />
            }
            {
                finished &&
                <>
                    {
                        !!succeded &&
                        <Text fontSize="14px">
                            { `${ t('Agregados') }: ${ succeded }` }
                        </Text>
                    }
                    {
                        !!updated &&
                        <Text fontSize="14px">
                            { `${ t('Editados') }: ${ updated }` }
                        </Text>
                    }
                </>
            }
            <ErrorMessage relative error={ showError } marginLeft={ 0 } marginTop={ 10 } />
            {
                !isEmpty(importErrors) &&
                <>
                    <Table
                        config={ DOWNLOAD_HEADERS }
                        data={ importErrors }
                        width="100%"
                    />
                    <DownloadButton
                        danger
                        caption={ t('Descargar errores') }
                        data={ importErrors }
                        filename={ t('Errores de importación') }
                        fontSize="12px"
                        headers={ DOWNLOAD_HEADERS }
                        margin="0px 0px 10px 0px"
                        separator=";"
                        size={ 20 }
                        target="_blank"
                    />
                </>
            }
            <ButtonFooter margin="20px 0px 0px 0px">
                {
                    (!finished || !isEmpty(importErrors))
                        ? <>
                            {
                                !importLoading &&
                                <ExpandButton
                                    danger
                                    caption={ t('Cancelar') }
                                    disabled={ !!importLoading }
                                    size={ 100 }
                                    onClick={ onToggle }
                                />
                            }
                            <ExpandButton
                                caption={ t('Importar') }
                                disabled={ !file || importLoading }
                                loading={ importUploading }
                                size={ 100 }
                                onClick={ onFileUpload }
                            />
                        </>
                        : <ExpandButton
                            caption={ t('Cerrar') }
                            size={ 100 }
                            onClick={ onToggle }
                        />
                }
            </ButtonFooter>
        </Modal>
    );
};

PatientImportModal.propTypes = {
    active: PropTypes.bool,
    onToggle: PropTypes.func
};

export default PatientImportModal;
